import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useSettings, useSeo } from "@/context/SettingsContext";
import { PageHero, FinalCta, SectionTitle } from "@/components/site/Blocks";
import { Steps } from "@/pages/Home";
import { money } from "@/lib/api";
import { STRIPES_IMG } from "@/lib/constants";

export default function HowItWorks() {
  const { settings } = useSettings();
  useSeo("How It Works", "How the $1 at-home lawn mower trial works: request, test at home, then buy or return.");
  const timeline = [
    ["You submit a request", "Tell us about your lawn and the mower you'd like to try. This is a request — not a confirmed booking — and you're not charged."],
    ["We review and contact you", "A team member checks eligibility, your service area and mower availability, then contacts you by your preferred method."],
    ["We confirm and collect the fee", `Once a mower and date are confirmed, we'll explain the ${money(settings.trial_fee)} trial fee and any other disclosed costs before you pay.`],
    ["Delivery or pickup", "We arrange delivery or pickup based on availability. The mower's condition is recorded at hand-over."],
    [`Your ${settings.trial_days}-day trial`, "Mow your own lawn. Try the controls, the cut height, the bagging — everything."],
    ["Buy it or return it", "Love it? Purchase at the listed price. Not for you? Tell us before the trial ends and we'll schedule collection."],
  ];
  return (
    <>
      <PageHero eyebrow="How it works" title="Test it on your grass, then decide." text="A straightforward, transparent process from your first request to your final decision." />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8"><Steps /></section>
      <section className="bg-slate-50 py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionTitle eyebrow="Step by step" title="What happens after you request" />
            <ol className="mt-10 space-y-0 border-l-2 border-emerald-200">
              {timeline.map(([t, d], i) => (
                <li key={t} className="relative pb-9 pl-8 last:pb-0">
                  <span className="absolute -left-[13px] top-0 grid h-6 w-6 place-items-center rounded-full bg-emerald-700 text-xs font-bold text-white">{i + 1}</span>
                  <h3 className="font-display text-lg font-bold text-slate-900">{t}</h3>
                  <p className="mt-1.5 leading-relaxed text-slate-600">{d}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="space-y-6">
            <img src={STRIPES_IMG} alt="Lawn with mowing stripes" loading="lazy" className="aspect-[4/3] w-full rounded-2xl object-cover" />
            <div className="rounded-2xl border border-slate-200 bg-white p-8">
              <h3 className="font-display text-xl font-bold text-slate-900">Good to know</h3>
              <ul className="mt-4 space-y-3 text-slate-700">
                {["Submitting the form never charges your card.", "Trials depend on mower availability and your service area.", "Delivery availability and any fees are confirmed before booking.", "Return and collection steps are set out in our Trial and Return Policy."].map((t) => (
                  <li key={t} className="flex gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />{t}</li>
                ))}
              </ul>
              <Link to="/trial-return-policy" className="mt-6 inline-block font-semibold text-emerald-800">Read the Trial and Return Policy →</Link>
            </div>
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
